import { useState } from 'react';

import {
  DEFAULT_GEO_CONFIG,
  PostGeoConfig,
  PostGeoConfigLocation,
} from 'pages/Settings/GeoConfigSettings/api/useGeoConfig/useGeoConfig';
import { defaultProfile } from 'pages/Settings/GeoConfigSettings/NewProfileSidebar';
import { useGeoConfigContext } from 'pages/Settings/GeoConfigSettings/utils/GeoConfigContext';
import Accordion from '@lotlinx-aws/sparky-component-library/Sparky/Accordion';
import Button from '@lotlinx-aws/sparky-component-library/Sparky/Button';
import Message from '@lotlinx-aws/sparky-component-library/Sparky/Message';
import { MapLegendIcon } from '@lotlinx-aws/sparky-component-library/Sparky/SVGs/MapLegendIcon';
import { PlusIcon } from '@lotlinx-aws/sparky-component-library/Sparky/SVGs/PlusIcon';
import Text from '@lotlinx-aws/sparky-component-library/Sparky/Text';
import Tile from '@lotlinx-aws/sparky-component-library/Sparky/Tile';
import { useToast } from '@lotlinx-aws/sparky-component-library/Sparky/Toast';

import styles from './ConfigProfilesSidebar.module.scss';

export interface ConfigProfilesSidebarProps {
  isGeoConfigChanged: boolean;
  onSelectPin(location: PostGeoConfigLocation): void;
  onCreateNewProfile(profile: PostGeoConfig): void;
}

/**
 * ConfigProfilesSidebar
 */
export default function ConfigProfilesSidebar({
  isGeoConfigChanged,
  onSelectPin,
  onCreateNewProfile,
}: ConfigProfilesSidebarProps) {
  const {
    tempGeoConfigs,
    selectProfile,
    setSelectProfile,
    modifyingExistingProfile,
    setModifyingExistingProfile,
  } = useGeoConfigContext();
  const { addToast } = useToast();
  const [expandedSection, setExpandedSection] = useState<'active' | 'inactive' | undefined>(
    'active',
  );

  const defaultRadiusProfile = tempGeoConfigs?.find((p) => p.name === DEFAULT_GEO_CONFIG.name);
  const customProfiles = tempGeoConfigs?.filter((p) => p.name !== DEFAULT_GEO_CONFIG.name) ?? [];
  const activeProfiles = customProfiles.filter((p) => p.hasActiveCampaign);
  const inactiveProfiles = customProfiles.filter((p) => !p.hasActiveCampaign);

  function renderProfileTile(profile: PostGeoConfig) {
    return (
      <Tile
        key={profile.configId ?? profile.name}
        title={profile.name}
        icon={<MapLegendIcon color='white' />}
        iconClassName={styles.iconClassName}
        isSelected={
          profile.configId === selectProfile?.configId || profile.name === selectProfile?.name
        }
        disabled={modifyingExistingProfile}
        setSelected={setSelectProfile}
        value={profile}
        showSelectedIcon={false}
        onClick={() => {
          if (modifyingExistingProfile && isGeoConfigChanged) {
            addToast({
              status: 'warning',
              description: `Save or discard your changes before switching profiles`,
            });
          } else {
            onSelectPin(profile.locations[0]);
            setModifyingExistingProfile(false);
          }
        }}
        filled
      />
    );
  }

  return (
    <div className={styles.configProfilesSidebar}>
      <div className={styles.configProfilesHeader}>
        <Text color='secondary' size='20' fontWeight={7}>
          Configuration profiles
        </Text>
        <Text color='tertiary' size='12'>
          Click on one of your profiles to view it in the map
        </Text>
      </div>
      <div className={styles.configProfilesContent}>
        <div className={styles.geoConfigProfiles}>
          {defaultRadiusProfile && renderProfileTile(defaultRadiusProfile)}
          <Accordion
            title='Active Configurations'
            formatName={false}
            isExpanded={expandedSection === 'active'}
            showDivider={false}
            handleClick={() =>
              setExpandedSection(expandedSection === 'active' ? undefined : 'active')
            }
          >
            <div className={styles.accordionProfiles}>
              {activeProfiles.length > 0 ? (
                activeProfiles.map(renderProfileTile)
              ) : (
                <Text color='tertiary' size='12'>
                  No active profiles
                </Text>
              )}
            </div>
          </Accordion>
          <Accordion
            title='Inactive Configurations'
            formatName={false}
            isExpanded={expandedSection === 'inactive'}
            showDivider={false}
            handleClick={() =>
              setExpandedSection(expandedSection === 'inactive' ? undefined : 'inactive')
            }
          >
            <div className={styles.accordionProfiles}>
              {inactiveProfiles.length > 0 ? (
                inactiveProfiles.map(renderProfileTile)
              ) : (
                <Text color='tertiary' size='12'>
                  No inactive profiles
                </Text>
              )}
            </div>
          </Accordion>
        </div>
        <Button
          variant='outline'
          leftIcon={<PlusIcon />}
          onClick={() => {
            if (modifyingExistingProfile && isGeoConfigChanged) {
              addToast({
                status: 'warning',
                description: `Save or discard your changes before switching profiles`,
              });
            } else {
              onCreateNewProfile(defaultProfile);
              setModifyingExistingProfile(false);
            }
          }}
          className={styles.createNewProfileButton}
        >
          Create new profile
        </Button>
        <Message heading='Add custom profiles' category='info' variant='muted'>
          You can target custom radius' in your campaigns by creating a new profile. Your profiles
          listed here will be available to select when you create a campaign
        </Message>
      </div>
    </div>
  );
}
